/**
 * hooks/useDossierBuilder.ts
 *
 * Manages all state and actions for the "Constituer le dossier" flow:
 * - Derives initial DossierPiece list from DocumentEntry[]
 * - Handles piece reordering (drag-and-drop and arrow buttons)
 * - Handles label editing per piece
 * - Manages stamp option (independent from downloads)
 * - Fetches user's stamp config and dossier_stamp_enabled preference from Supabase on mount
 * - Persists stamp toggle preference to DB on each change (optimistic update)
 * - Provides 3 independent download functions: dossier PDF, bordereau PDF, bordereau Word
 * - Handles per-document exclusion (manual) and retry for failed conversions
 * - Preloads PDF buffers on mount for near-instant generation
 *
 * Updated Apr 2026: removed ZIP bundle + JSZip. Replaced with 3 independent downloads.
 * Each download has its own phase state (idle/downloading/generating/success/error).
 * bordereauFormat state removed (both formats always available simultaneously).
 * preloadBuffers now triggered at mount instead of on wizard step transition.
 * Updated Apr 2026: StampStatus extracted to src/types/dossier.ts (shared with DossierActionSidebar).
 * Updated Apr 2026: stamp toggle rollback now uses lastConfirmedStampRef (last server-confirmed value)
 *   instead of !val so concurrent-toggle failures roll back to the correct server state.
 * Updated Apr 2026: added moveDoc and reorderDocsInPiece for per-file reordering within a piece.
 * Updated Jun 2026: stamp config and toggle now use effectiveUserId so that in impersonation
 *   mode the admin sees and configures the impersonated user's stamp, not their own.
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import { useEffectiveUser } from './useEffectiveUser';
import { buildDossierPdf, preloadDossierPdfs, type StampConfig } from '../utils/pdfDossierBuilder';
import { generateBordereauDocBlob, generateBordereauPdfBytes } from '../utils/bordereauGenerator';
import { slugify } from '../utils/filenameNormalizer';
import type { DocumentEntry, DossierPiece, ExcludedDocItem, StampStatus } from '../types/dossier';
import { captureError } from '../utils/errorReporting';

const EDGE_FUNCTION_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/convert-documents`;
const ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

export type ExportPhase = 'idle' | 'downloading' | 'generating' | 'success' | 'error';

function buildPiecesFromDocuments(documents: DocumentEntry[]): DossierPiece[] {
  const pieceMap = new Map<string, DossierPiece>();

  for (const doc of documents) {
    const key = doc.fieldId;
    if (!pieceMap.has(key)) {
      pieceMap.set(key, {
        id: doc.fieldId,
        label: doc.fieldLabel,
        fieldPosition: doc.fieldPosition,
        docs: [],
      });
    }
    pieceMap.get(key)!.docs.push(doc);
  }

  return Array.from(pieceMap.values()).sort((a, b) => a.fieldPosition - b.fieldPosition);
}

/**
 * Merges updated document data into existing pieces, preserving user ordering and labels.
 * Only updates document properties (uploadStatus, pdfPath, etc.), never the piece structure.
 */
function mergePiecesWithDocuments(
  prevPieces: DossierPiece[],
  documents: DocumentEntry[],
): DossierPiece[] {
  const docMap = new Map<string, DocumentEntry>(documents.map((d) => [d.id, d]));

  const updated = prevPieces.map((piece) => ({
    ...piece,
    docs: piece.docs.map((d) => docMap.get(d.id) ?? d),
  }));

  const existingDocIds = new Set<string>(prevPieces.flatMap((p) => p.docs.map((d) => d.id)));
  const newDocs = documents.filter((d) => !existingDocIds.has(d.id));

  if (newDocs.length === 0) return updated;

  const newPieces = buildPiecesFromDocuments(newDocs);
  for (const newPiece of newPieces) {
    const existingPiece = updated.find((p) => p.id === newPiece.id);
    if (existingPiece) {
      existingPiece.docs.push(...newPiece.docs);
    } else {
      updated.push(newPiece);
    }
  }

  return updated;
}

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const SUCCESS_RESET_MS = 2500;

export function useDossierBuilder(documents: DocumentEntry[], clientName: string | null) {
  const { user } = useAuth();
  const { effectiveUserId } = useEffectiveUser();
  const { showToast } = useToast();

  const [pieces, setPieces] = useState<DossierPiece[]>([]);
  const [withStamp, setWithStamp] = useState(false);
  const [stampStatus, setStampStatus] = useState<StampStatus>({
    config: null,
    isLoading: true,
    isMissing: false,
    isIncomplete: false,
  });
  const [excludedDocIds, setExcludedDocIds] = useState<Set<string>>(new Set());
  const [retryingDocIds, setRetryingDocIds] = useState<Set<string>>(new Set());

  const [dossierPdfPhase, setDossierPdfPhase] = useState<ExportPhase>('idle');
  const [dossierPdfProgress, setDossierPdfProgress] = useState<{ done: number; total: number }>({ done: 0, total: 0 });
  const [bordereauPdfPhase, setBordereauPdfPhase] = useState<ExportPhase>('idle');
  const [bordereauDocPhase, setBordereauDocPhase] = useState<ExportPhase>('idle');

  const documentsDigestRef = useRef<string | null>(null);
  const toggleRequestIdRef = useRef(0);
  const isInitializedRef = useRef(false);
  const lastConfirmedStampRef = useRef<boolean>(false);
  const preloadedBuffersRef = useRef<Map<string, ArrayBuffer>>(new Map());
  const [isPreloading, setIsPreloading] = useState(false);

  useEffect(() => {
    const digest = documents
      .map((d) => `${d.id}:${d.uploadStatus}:${d.pdfPath ?? ''}`)
      .join('|');

    if (digest === documentsDigestRef.current) return;
    documentsDigestRef.current = digest;

    if (!isInitializedRef.current) {
      isInitializedRef.current = true;
      setPieces(buildPiecesFromDocuments(documents));
    } else {
      setPieces((prev) => mergePiecesWithDocuments(prev, documents));
    }
  }, [documents]);

  useEffect(() => {
    if (!effectiveUserId) return;
    (async () => {
      const { data, error } = await supabase
        .from('users')
        .select('stamp_title, stamp_toque_number, dossier_stamp_enabled')
        .eq('id', effectiveUserId)
        .maybeSingle();

      if (error) {
        console.error('[useDossierBuilder] Failed to load stamp config for user', effectiveUserId, error);
        setStampStatus({ config: null, isLoading: false, isMissing: false, isIncomplete: false });
        setWithStamp(false);
        return;
      }

      const title = data?.stamp_title?.trim() ?? '';
      const toque = data?.stamp_toque_number?.trim() ?? '';
      const isMissing = !title && !toque;
      const isIncomplete = (!title || !toque) && !isMissing;
      const config: StampConfig | null = !isMissing && !isIncomplete
        ? { title, toqueNumber: toque }
        : null;

      const confirmedStamp = data?.dossier_stamp_enabled ?? true;
      lastConfirmedStampRef.current = confirmedStamp;
      setStampStatus({ config, isLoading: false, isMissing, isIncomplete });
      setWithStamp(confirmedStamp);
    })();
  }, [effectiveUserId]);

  const updateLabel = useCallback((pieceId: string, label: string) => {
    setPieces((prev) => prev.map((p) => (p.id === pieceId ? { ...p, label } : p)));
  }, []);

  const movePiece = useCallback((pieceId: string, direction: 'up' | 'down') => {
    setPieces((prev) => {
      const includedIds = new Set(
        prev
          .filter((p) => p.docs.some(
            (d) => d.uploadStatus === 'completed' && d.pdfPath && !excludedDocIds.has(d.id),
          ))
          .map((p) => p.id),
      );
      const sorted = prev.filter((p) => includedIds.has(p.id));
      const idx = sorted.findIndex((p) => p.id === pieceId);
      if (idx < 0) return prev;
      const newIdx = direction === 'up' ? idx - 1 : idx + 1;
      if (newIdx < 0 || newIdx >= sorted.length) return prev;
      const reordered = [...sorted];
      [reordered[idx], reordered[newIdx]] = [reordered[newIdx], reordered[idx]];
      const reorderedIds = new Set(reordered.map((p) => p.id));
      return [...reordered, ...prev.filter((p) => !reorderedIds.has(p.id))];
    });
  }, [excludedDocIds]);

  const reorderPieces = useCallback((orderedIds: string[]) => {
    setPieces((prev) => {
      const map = new Map(prev.map((p) => [p.id, p]));
      const reordered = orderedIds.map((id) => map.get(id)).filter(Boolean) as DossierPiece[];
      const reorderedIds = new Set(orderedIds);
      return [...reordered, ...prev.filter((p) => !reorderedIds.has(p.id))];
    });
  }, []);

  const moveDoc = useCallback((pieceId: string, docId: string, direction: 'up' | 'down') => {
    setPieces((prev) =>
      prev.map((p) => {
        if (p.id !== pieceId) return p;
        const idx = p.docs.findIndex((d) => d.id === docId);
        if (idx < 0) return p;
        const newIdx = direction === 'up' ? idx - 1 : idx + 1;
        if (newIdx < 0 || newIdx >= p.docs.length) return p;
        const reordered = [...p.docs];
        [reordered[idx], reordered[newIdx]] = [reordered[newIdx], reordered[idx]];
        return { ...p, docs: reordered };
      }),
    );
  }, []);

  const reorderDocsInPiece = useCallback((pieceId: string, orderedDocIds: string[]) => {
    setPieces((prev) =>
      prev.map((p) => {
        if (p.id !== pieceId) return p;
        const docMap = new Map(p.docs.map((d) => [d.id, d]));
        const reordered = orderedDocIds
          .map((id) => docMap.get(id))
          .filter(Boolean) as DocumentEntry[];
        const reorderedIds = new Set(orderedDocIds);
        return { ...p, docs: [...reordered, ...p.docs.filter((d) => !reorderedIds.has(d.id))] };
      }),
    );
  }, []);

  const handleToggleStamp = useCallback((val: boolean) => {
    setWithStamp(val);
    if (!effectiveUserId) return;
    const requestId = ++toggleRequestIdRef.current;
    supabase
      .from('users')
      .update({ dossier_stamp_enabled: val })
      .eq('id', effectiveUserId)
      .then(({ error }) => {
        if (requestId !== toggleRequestIdRef.current) return;
        if (error) {
          setWithStamp(lastConfirmedStampRef.current);
          showToast('Impossible de sauvegarder la préférence du tampon', 'error');
        } else {
          lastConfirmedStampRef.current = val;
        }
      });
  }, [effectiveUserId, showToast]);

  const excludeDoc = useCallback((docId: string) => {
    setExcludedDocIds((prev) => new Set([...prev, docId]));
  }, []);

  const includeDoc = useCallback((docId: string) => {
    setExcludedDocIds((prev) => {
      const next = new Set(prev);
      next.delete(docId);
      return next;
    });
  }, []);

  const retryDoc = useCallback(async (docId: string, requestId: string) => {
    setRetryingDocIds((prev) => new Set([...prev, docId]));
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('Not authenticated');

      const resp = await fetch(EDGE_FUNCTION_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'Apikey': ANON_KEY,
        },
        body: JSON.stringify({ request_id: requestId, document_ids: [docId] }),
      });

      if (!resp.ok) {
        const bodyText = await resp.text();
        throw new Error(`Conversion failed (${resp.status}): ${bodyText}`);
      }

      const result = await resp.json();
      if (result.converted > 0) {
        showToast('Document converti avec succès', 'success');
      } else {
        showToast(`Échec : ${result.errors?.[0] ?? 'Erreur inconnue'}`, 'error');
      }
    } catch (err) {
      console.error('[useDossierBuilder] Document conversion retry error:', err);
      captureError(err, { hook: 'useDossierBuilder', action: 'retryDoc', docId, requestId });
      showToast('Erreur lors de la conversion', 'error');
    } finally {
      setRetryingDocIds((prev) => {
        const next = new Set(prev);
        next.delete(docId);
        return next;
      });
    }
  }, [showToast]);

  const includedPieces = useMemo(
    () =>
      pieces
        .map((p) => ({
          ...p,
          docs: p.docs.filter(
            (d) => d.uploadStatus === 'completed' && d.pdfPath && !excludedDocIds.has(d.id),
          ),
        }))
        .filter((p) => p.docs.length > 0),
    [pieces, excludedDocIds],
  );

  const failedDocs = useMemo((): ExcludedDocItem[] => {
    const result: ExcludedDocItem[] = [];
    for (const p of pieces) {
      for (const d of p.docs) {
        if (d.uploadStatus === 'failed') {
          result.push({ doc: d, pieceLabel: p.label, pieceId: p.id });
        }
      }
    }
    return result;
  }, [pieces]);

  const preloadBuffers = useCallback(async () => {
    const snapshot = includedPieces.map((p) => ({ ...p, docs: [...p.docs] }));
    if (snapshot.length === 0) return;
    setIsPreloading(true);
    try {
      const buffers = await preloadDossierPdfs(snapshot);
      preloadedBuffersRef.current = buffers;
    } catch (err) {
      console.error('[useDossierBuilder] Preload failed:', err);
      captureError(err, { hook: 'useDossierBuilder', action: 'preloadBuffers' });
    } finally {
      setIsPreloading(false);
    }
  }, [includedPieces]);

  const downloadDossierPdf = useCallback(async () => {
    if (includedPieces.length === 0) {
      showToast('Aucune pièce disponible pour le dossier', 'error');
      return;
    }

    setDossierPdfPhase('downloading');
    setDossierPdfProgress({ done: 0, total: 0 });

    try {
      const snapshot = includedPieces.map((p) => ({ ...p, docs: [...p.docs] }));
      const slug = clientName ? slugify(clientName) : 'client';

      const pdfResult = await buildDossierPdf({
        pieces: snapshot,
        stampConfig: withStamp ? stampStatus.config : null,
        includeBordereau: false,
        clientName,
        preloadedBuffers: preloadedBuffersRef.current.size > 0 ? preloadedBuffersRef.current : undefined,
        onProgress: (done, total) => {
          setDossierPdfProgress({ done, total });
          if (done === total) setDossierPdfPhase('generating');
        },
      });

      triggerDownload(
        new Blob([pdfResult.pdfBytes], { type: 'application/pdf' }),
        `dossier-${slug}.pdf`,
      );

      setDossierPdfPhase('success');
      setTimeout(() => setDossierPdfPhase('idle'), SUCCESS_RESET_MS);

      if (pdfResult.failedPieces.length > 0) {
        const downloadFailed = pdfResult.failedPieces.filter((p) => p.reason === 'download_failed');
        const corrupted = pdfResult.failedPieces.filter((p) => p.reason === 'corrupted');

        if (downloadFailed.length > 0) {
          const labels = downloadFailed.map((p) => `« ${p.pieceName} »`).join(', ');
          showToast(`Pièce(s) non téléchargeable(s) : ${labels}. Vérifiez votre connexion et réessayez.`, 'warning');
        }
        if (corrupted.length > 0) {
          const labels = corrupted.map((p) => `« ${p.pieceName} »`).join(', ');
          showToast(`Pièce(s) illisible(s) : ${labels}. Le fichier est peut-être endommagé — demandez à votre client de le renvoyer.`, 'warning');
        }
      }
    } catch (err) {
      console.error('[useDossierBuilder] Dossier PDF generation error:', err);
      captureError(err, { hook: 'useDossierBuilder', action: 'downloadDossierPdf' });
      showToast('Une erreur est survenue lors de la génération du dossier', 'error');
      setDossierPdfPhase('error');
      setTimeout(() => setDossierPdfPhase('idle'), 3000);
    }
  }, [includedPieces, withStamp, stampStatus.config, clientName, showToast]);

  const downloadBordereauPdf = useCallback(async () => {
    if (includedPieces.length === 0) {
      showToast('Aucune pièce disponible pour le bordereau', 'error');
      return;
    }

    setBordereauPdfPhase('generating');
    try {
      const snapshot = includedPieces.map((p) => ({ ...p, docs: [...p.docs] }));
      const slug = clientName ? slugify(clientName) : 'client';
      const bytes = await generateBordereauPdfBytes(snapshot);
      triggerDownload(new Blob([bytes], { type: 'application/pdf' }), `bordereau-${slug}.pdf`);
      setBordereauPdfPhase('success');
      setTimeout(() => setBordereauPdfPhase('idle'), SUCCESS_RESET_MS);
    } catch (err) {
      console.error('[useDossierBuilder] Bordereau PDF error:', err);
      captureError(err, { hook: 'useDossierBuilder', action: 'downloadBordereauPdf' });
      showToast('Erreur lors de la génération du bordereau PDF', 'error');
      setBordereauPdfPhase('error');
      setTimeout(() => setBordereauPdfPhase('idle'), 3000);
    }
  }, [includedPieces, clientName, showToast]);

  const downloadBordereauDoc = useCallback(async () => {
    if (includedPieces.length === 0) {
      showToast('Aucune pièce disponible pour le bordereau', 'error');
      return;
    }

    setBordereauDocPhase('generating');
    try {
      const snapshot = includedPieces.map((p) => ({ ...p, docs: [...p.docs] }));
      const slug = clientName ? slugify(clientName) : 'client';
      const blob = generateBordereauDocBlob(snapshot);
      triggerDownload(blob, `bordereau-${slug}.doc`);
      setBordereauDocPhase('success');
      setTimeout(() => setBordereauDocPhase('idle'), SUCCESS_RESET_MS);
    } catch (err) {
      console.error('[useDossierBuilder] Bordereau Word error:', err);
      captureError(err, { hook: 'useDossierBuilder', action: 'downloadBordereauDoc' });
      showToast('Erreur lors de la génération du bordereau Word', 'error');
      setBordereauDocPhase('error');
      setTimeout(() => setBordereauDocPhase('idle'), 3000);
    }
  }, [includedPieces, clientName, showToast]);

  return {
    pieces,
    includedPieces,
    excludedDocIds,
    failedDocs,
    withStamp,
    stampStatus,
    dossierPdfPhase,
    dossierPdfProgress,
    bordereauPdfPhase,
    bordereauDocPhase,
    retryingDocIds,
    isPreloading,
    updateLabel,
    movePiece,
    reorderPieces,
    moveDoc,
    reorderDocsInPiece,
    handleToggleStamp,
    excludeDoc,
    includeDoc,
    retryDoc,
    preloadBuffers,
    downloadDossierPdf,
    downloadBordereauPdf,
    downloadBordereauDoc,
  };
}
